import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { FluentProvider } from '@fluentui/react-components';

import { bayerDarkTheme, bayerLightTheme } from './theme';
import { AuthProvider } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';
import RequireAuth from './auth/RequireAuth';
import DataService, { IDataService } from './services/DataService';
import { DataServiceProvider } from './app/DataServiceContext';
import { ToastProvider } from './app/ToastContext';
import AppShell from './app/AppShell';
import PollsPage from './pages/PollsPage';
import PollDetailsPage from './pages/PollDetailsPage';
import PollWizardPage from './pages/PollWizardPage';
import PollEditPage from './pages/PollEditPage';
import PollResultsPage from './pages/PollResultsPage';
import PollQrPage from './pages/PollQrPage';
import PollBrandingPage from './pages/PollBrandingPage';
import TemplatesPage from './pages/TemplatesPage';
import NotFoundPage from './pages/NotFoundPage';

/** Follows the OS colour scheme; the brand ramp lives in theme.ts. */
function usePrefersDark(): boolean {
    const [prefersDark, setPrefersDark] = React.useState(
        () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false,
    );

    React.useEffect(() => {
        const query = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (!query) return;
        const onChange = (event: MediaQueryListEvent): void => setPrefersDark(event.matches);
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    return prefersDark;
}

/** One API client for the whole app. */
const dataService: IDataService = new DataService();

const LEGACY_VIEWS: Record<string, (pollId?: string) => string | undefined> = {
    results: (id) => (id ? `/polls/${id}/results` : undefined),
    editPoll: (id) => (id ? `/polls/${id}/edit` : undefined),
    qrCode: (id) => (id ? `/polls/${id}/qr` : undefined),
    branding: (id) => (id ? `/polls/${id}/branding` : undefined),
    newQuickPoll: () => '/polls/new/quick',
    newComplexPoll: () => '/polls/new/complex',
    templates: () => '/templates',
};

/**
 * The previous build addressed sub-views through the location hash
 * (`#view=results&pollId=…`). Old bookmarks land here and get forwarded to the
 * equivalent route.
 */
const LegacyHashRedirect: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    React.useEffect(() => {
        const hash = location.hash.replace(/^#/, '');
        if (!hash) return;
        const params = new URLSearchParams(hash);
        const view = params.get('view');
        const pollId = params.get('pollId') ?? undefined;
        const target = view ? LEGACY_VIEWS[view]?.(pollId ? encodeURIComponent(pollId) : undefined) : undefined;
        if (target) navigate(target, { replace: true });
    }, [location.hash, navigate]);

    return null;
};

const App: React.FC = () => {
    const prefersDark = usePrefersDark();

    return (
        <FluentProvider theme={prefersDark ? bayerDarkTheme : bayerLightTheme} style={{ minHeight: '100dvh' }}>
            <BrowserRouter>
                <AuthProvider>
                    <DataServiceProvider service={dataService}>
                        <ToastProvider>
                            <LegacyHashRedirect />
                            <Routes>
                                <Route path="/login" element={<LoginPage />} />
                                <Route element={<RequireAuth><AppShell /></RequireAuth>}>
                                    <Route index element={<Navigate to="/polls" replace />} />
                                    <Route path="/polls" element={<PollsPage />} />
                                    <Route path="/polls/new/:mode" element={<PollWizardPage />} />
                                    <Route path="/polls/:id" element={<PollDetailsPage />} />
                                    <Route path="/polls/:id/edit" element={<PollEditPage />} />
                                    <Route path="/polls/:id/results" element={<PollResultsPage />} />
                                    <Route path="/polls/:id/qr" element={<PollQrPage />} />
                                    <Route path="/polls/:id/branding" element={<PollBrandingPage />} />
                                    <Route path="/templates" element={<TemplatesPage />} />
                                    <Route path="*" element={<NotFoundPage />} />
                                </Route>
                            </Routes>
                        </ToastProvider>
                    </DataServiceProvider>
                </AuthProvider>
            </BrowserRouter>
        </FluentProvider>
    );
};

export default App;
